import type { GrantCode } from '../../auth/grants'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useToast } from './ToastProvider'
import { downloadAttachment } from '../../api/attachments'
import { useGrants } from '../../auth/useCurrentUser'

interface Props {
  id: number
  fileName: string
  /** true → yalnız ikon (etiketsiz); tablo satır-işlem butonları için. Varsayılan etiketli. */
  iconOnly?: boolean
  /** Buton sınıfını geçersiz kılar (ör. satır-işlem `minutes-remove-btn`); verilmezse `ui-attach-btn`. */
  className?: string
  /**
   * Bu ekin bağlamında indirme için GEREKEN yetki — sunucudaki bağlam→yetki haritasının istemci
   * karşılığı (`AttachmentController.REQUIRED_GRANT_BY_CONTEXT`). Yetki yoksa buton hiç render edilmez.
   */
  requiredGrant: GrantCode
}

/**
 * Ortak "İndir" butonu — dosya ekini kimlik-doğrulamalı ({@code apiClient} yetkilendirme başlığıyla) indirir. Düz
 * {@code <a href>} yerine kullanılır: tarayıcı navigasyonu OIDC token'ı taşımaz ve backend
 * {@code UNAUTHENTICATED} döner. Görsel dil {@code ui-attach-btn} ile aynıdır; hata toast ile bildirilir.
 * {@code iconOnly} ile etiketsiz (satır-işlem) varyant.
 *
 * <h2>Yetki kapısı (2026-08-25)</h2>
 *
 * Yetkisi olmayan kullanıcıda buton <b>hiç render edilmez</b>. Öncesinde kapı YOKTU: buton herkese
 * görünüyor, tıklayan 403 alıp "indirme başarısız" toast'ı görüyordu — kullanıcı bildirimi, GM rolü.
 *
 * <p><b>Gereken yetki bağlama göre değişir</b>, o yüzden {@link Props.requiredGrant} ile çağırandan
 * gelir: toplantı/tutanak/not ekinde {@code MEETING_FILE_DOWNLOAD}, etkinlik ekinde
 * {@code ACTIVITY_VIEW}. Bu, sunucudaki {@code AttachmentController.REQUIRED_GRANT_BY_CONTEXT}
 * haritasının aynısıdır — buton <b>tam olarak istek başarılı olacağı durumda</b> görünür.
 *
 * <p>İlk hâlde koşul, sunucunun eski tek koruması olan {@code HAS_ATTACHMENT_READ} bileşiğini
 * (beş yetkiden herhangi biri) aynalıyordu. O bileşik fazla genişti: {@code MEETING_FILE_DOWNLOAD}
 * alınsa bile talep/etkinlik okuma yetkisi toplantı dosyasını açıyordu. Sunucu bağlama duyarlı hâle
 * getirilince istemci de daraltıldı.
 */
export default function AttachmentDownloadButton({
  id,
  fileName,
  iconOnly,
  className,
  requiredGrant,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const toast = useToast()
  const { hasGrant } = useGrants()
  const [busy, setBusy] = useState(false)

  const handleDownload = async () => {
    setBusy(true)
    try {
      await downloadAttachment(id, fileName)
    } catch {
      toast.error(t('common.downloadError'))
    } finally {
      setBusy(false)
    }
  }

  if (!hasGrant(requiredGrant)) {
    return null
  }

  return (
    <button
      type="button"
      className={className ?? 'ui-attach-btn'}
      onClick={handleDownload}
      disabled={busy}
      aria-label={iconOnly ? t('common.download') : undefined}
      title={iconOnly ? t('common.download') : undefined}
    >
      <i className="pi pi-download" aria-hidden="true" />
      {!iconOnly && <span>{t('common.download')}</span>}
    </button>
  )
}
